import { Button } from "@kovernow/react-ui/components/button";
import { useId } from "react";
import { useMutationGate } from "../core/mutation-gate";
import { useT } from "../core/use-t";

/**
 * A button that is unavailable while busy or offline: `aria-disabled` plus
 * visible reason text, never native `disabled` (UX contract).
 */
export function GatedButton({
  busy,
  label,
  onClick,
  type = "button",
  variant = "default",
  unavailableReason,
}: {
  busy?: boolean;
  label: string;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "default" | "cta" | "outline";
  /** Extra reason the action is unavailable (e.g. terms not accepted). */
  unavailableReason?: string;
}) {
  const gate = useMutationGate();
  const t = useT();
  const id = useId();
  const reason = gate.blocked ? gate.reason : unavailableReason;
  const unavailable = gate.blocked || busy || !!unavailableReason;
  const reasonId = reason ? `${id}-reason` : undefined;
  return (
    <div className="flex flex-col gap-1">
      <Button
        type={type}
        variant={variant}
        size="touch"
        className="w-full"
        aria-disabled={unavailable ? true : undefined}
        aria-describedby={reasonId}
        onClick={(e) => {
          if (unavailable) {
            e.preventDefault();
            return;
          }
          onClick?.();
        }}
      >
        {busy ? t("common.loading") : label}
      </Button>
      {reason ? (
        <p id={reasonId} className="text-sm text-muted-foreground">
          {reason}
        </p>
      ) : null}
    </div>
  );
}
